import { useEffect } from "react";
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  CircleDot,
  Files,
  FolderOpen,
  Headphones,
  MessageCircleMore,
  Monitor,
  Network,
  Orbit,
  Rocket,
  ShieldCheck,
  Sparkles,
  Target,
  Telescope,
  Ticket,
  UsersRound,
  Workflow,
} from "lucide-react";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";
import Reveal from "./Reveal";
import Breadcrumbs from "./Breadcrumbs";

const productCards = [
  {
    className: "connect",
    icon: MessageCircleMore,
    name: "CONNECT",
    title: "Team Messaging & Collaboration",
    copy: "Bring conversations, channels, calls, and teamwork together.",
    href: "/connect",
  },
  {
    className: "desk",
    icon: Ticket,
    name: "DESK",
    title: "Ticketing & Workflow Management",
    copy: "Organize requests, assign work, and keep progress visible.",
    href: "/desk",
  },
  {
    className: "assist",
    icon: Monitor,
    name: "ASSIST",
    title: "Remote Support & Device Access",
    copy: "Securely connect to devices, resolve issues, and support users.",
    href: "/assist",
  },
];

const values = [
  { icon: Network, title: "Connected Thinking", copy: "Better work starts with better connections between people, information, and technology. Every product is designed to bring work together, not spread it apart." },
  { icon: Sparkles, title: "Simplicity", copy: "Powerful software should feel effortless. We reduce complexity so teams can focus on collaboration, productivity, and meaningful work." },
  { icon: ShieldCheck, title: "Reliability", copy: "Organizations rely on software every day. We build dependable solutions teams can trust for secure, consistent, uninterrupted work." },
  { icon: BarChart3, title: "Continuous Progress", copy: "Technology never stands still, and neither do we. We continuously improve our platform to meet the evolving needs of modern workplaces." },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  return (
    <div className="about-page-shell">
      <SiteHeader isProductPage isHeroPage />

      <main className="about-page">
        <section className="about-hero">
          <div className="about-orbit-bg about-orbit-bg--one" />
          <div className="about-orbit-bg about-orbit-bg--two" />
          <div className="about-container about-hero-grid">
            <Reveal className="about-hero-copy">
              <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "About Us" }]} />
              <span className="about-kicker">ABOUT US</span>
              <h1>Building the Future of <em>Connected Work.</em></h1>
              <div className="about-title-line" />
              <p>Cullath is building a connected business software ecosystem that brings communication, remote support, and workflow management together in one growing platform.</p>
              <p>We help modern teams simplify work, reduce complexity, and stay connected every day.</p>
              <div className="about-hero-actions">
                <a className="about-btn about-btn--primary" href="#ecosystem">Explore Our Products <ArrowRight size={19} /></a>
                <a className="about-btn about-btn--secondary" href="/contact-us">Request a Demo <ArrowRight size={18} /></a>
              </div>
            </Reveal>

            <Reveal className="about-hero-visual" delay={120}>
              <div className="hero-orbit hero-orbit--outer" />
              <div className="hero-orbit hero-orbit--middle" />
              <div className="hero-orbit hero-orbit--inner" />
              <span className="hero-dot hero-dot--one" />
              <span className="hero-dot hero-dot--two" />
              <span className="hero-dot hero-dot--three" />
              <div className="hero-logo-core"><img src="/cullath-logo.svg" alt="Cullath" /></div>
              <article className="orbit-product orbit-product--connect"><span><MessageCircleMore /></span><div><strong>CONNECT</strong><small>Team messaging and collaboration</small></div></article>
              <article className="orbit-product orbit-product--assist"><span><Monitor /></span><div><strong>ASSIST</strong><small>Remote support and secure access</small></div></article>
              <article className="orbit-product orbit-product--desk"><span><Ticket /></span><div><strong>DESK</strong><small>Ticketing and workflow management</small></div></article>
            </Reveal>
          </div>
          <div className="about-hero-wave" />
        </section>

        <section className="about-section who-section">
          <div className="about-container who-grid">
            <Reveal className="who-copy">
              <span className="about-kicker">WHO WE ARE</span>
              <h2>Who We Are</h2>
              <p>Cullath is a business software company focused on creating connected applications for the modern workplace.</p>
              <p>Instead of relying on multiple disconnected tools, we believe teams should be able to communicate, support users, and manage work from one unified ecosystem.</p>
              <p>Starting with Cullath Connect, Cullath Desk, and Cullath Assist, we are building a growing family of business applications designed to work better together.</p>
              <div className="who-pillars">
                <article><UsersRound /><strong>People First</strong><small>We build for teams and the people who power them.</small></article>
                <article><ShieldCheck /><strong>Unified & Secure</strong><small>One connected ecosystem with enterprise-grade security.</small></article>
                <article><Rocket /><strong>Built to Grow</strong><small>Scalable today, ready for tomorrow.</small></article>
              </div>
            </Reveal>

            <Reveal className="who-visual" delay={120}>
              <div className="who-ring who-ring--outer" />
              <div className="who-ring who-ring--inner" />
              <div className="who-center"><img src="/cullath-logo.svg" alt="Cullath" /><strong>Cullath</strong><small>One Connected Workplace</small></div>
              <article className="who-node who-node--top"><span><UsersRound /></span><strong>Your Team</strong><small>Communicate, collaborate and stay connected.</small></article>
              <article className="who-node who-node--left"><span><CheckCircle2 /></span><strong>Your Work</strong><small>Organize tasks, projects, and progress.</small></article>
              <article className="who-node who-node--right"><span><Workflow /></span><strong>Your Business</strong><small>Improve outcomes and grow together.</small></article>
              <article className="who-node who-node--bottom"><span><FolderOpen /></span><strong>Your Files</strong><small>Store, share, and access information securely.</small></article>
              <span className="mini-node mini-node--one"><MessageCircleMore /></span>
              <span className="mini-node mini-node--two"><Headphones /></span>
              <span className="mini-node mini-node--three"><BarChart3 /></span>
              <span className="mini-node mini-node--four"><Files /></span>
            </Reveal>
          </div>
        </section>

        <section className="about-section purpose-section">
          <div className="about-container">
            <Reveal className="about-section-heading centered">
              <span className="about-kicker">OUR PURPOSE</span>
              <h2>Mission & Vision</h2>
              <p>Our mission guides what we do today. Our vision inspires where we are headed tomorrow.</p>
            </Reveal>
            <div className="purpose-grid">
              <Reveal className="purpose-card purpose-card--mission">
                <span className="purpose-icon"><Target /></span>
                <small>MISSION</small>
                <h3>Helping teams work together with less friction.</h3>
                <p>We build connected software that simplifies communication, remote support, and workflow management so organizations can focus on getting work done.</p>
                <div><span><MessageCircleMore /> Seamless Communication</span><span><Monitor /> Smart Remote Support</span><span><CheckCircle2 /> Effortless Workflows</span></div>
              </Reveal>
              <span className="purpose-link"><Orbit /></span>
              <Reveal className="purpose-card purpose-card--vision" delay={100}>
                <span className="purpose-icon"><Telescope /></span>
                <small>VISION</small>
                <h3>A workplace where every tool works together.</h3>
                <p>Our vision is to create a growing ecosystem of business applications that keeps people, processes, and technology connected through one seamless platform.</p>
                <div><span><UsersRound /> Connected People</span><span><Workflow /> Integrated Processes</span><span><ShieldCheck /> Unified Technology</span></div>
              </Reveal>
            </div>
            <div className="purpose-note"><Sparkles /> One Purpose. One Platform. Limitless Possibilities.</div>
          </div>
        </section>

        <section className="about-section ecosystem-section" id="ecosystem">
          <div className="about-container">
            <Reveal className="about-section-heading centered">
              <span className="about-kicker">OUR ECOSYSTEM</span>
              <h2>One Platform. Three Connected Products.</h2>
              <p>Explore the three products that power the Cullath workplace ecosystem.</p>
            </Reveal>
            <div className="ecosystem-grid">
              {productCards.map(({ className, icon: Icon, name, title, copy, href }, index) => (
                <Reveal className={`ecosystem-card ecosystem-card--${className}`} delay={index * 80} key={name}>
                  <span className="ecosystem-icon"><Icon /></span>
                  <h3>{name}</h3><strong>{title}</strong><p>{copy}</p>
                  <a href={href}>Learn More <ArrowRight size={18} /></a>
                </Reveal>
              ))}
            </div>
            <Reveal className="ecosystem-future">
              <span><Sparkles /></span><div><strong>Growing Ecosystem</strong><small>More products will join the Cullath ecosystem as we expand our connected workplace platform.</small></div><b>+ Future Innovations</b>
            </Reveal>
          </div>
        </section>

        <section className="about-section values-section">
          <div className="about-container">
            <Reveal className="about-section-heading centered">
              <span className="about-kicker">OUR VALUES</span>
              <h2>The Principles Behind Everything We Build.</h2>
              <p>Every decision is guided by a commitment to building connected, reliable, and intuitive software.</p>
            </Reveal>
            <div className="values-grid">
              {values.map(({ icon: Icon, title, copy }, index) => (
                <Reveal className="value-card" delay={index * 70} key={title}>
                  <span><Icon /></span><div><h3>{title}</h3><i /><p>{copy}</p></div>
                </Reveal>
              ))}
            </div>
            <Reveal className="values-quote"><strong>“</strong><span>We are building more than software—<br />we are building a <em>connected workplace</em> for the future.</span></Reveal>
          </div>
        </section>

        <section className="about-cta-wrap">
          <div className="about-container">
            <Reveal className="about-cta">
              <div className="cta-orbit cta-orbit--one" /><div className="cta-orbit cta-orbit--two" />
              <span className="cta-logo"><img src="/cullath-logo.svg" alt="" /></span>
              <span className="cta-floating cta-floating--one"><MessageCircleMore /></span>
              <span className="cta-floating cta-floating--two"><Monitor /></span>
              <span className="cta-floating cta-floating--three"><Ticket /></span>
              <h2>Ready to<br />Bring <em>Work</em> Into <em>Orbit?</em></h2>
              <p>Discover how Cullath helps modern teams communicate, support, and manage work from one connected platform.</p>
              <div><a href="#ecosystem">Explore Products <ArrowRight /></a><a href="/contact-us">Request a Demo <ArrowRight /></a></div>
            </Reveal>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
